import { stringify } from "csv-stringify/lib/index";
import * as luxon from "luxon";
import React from "react";
import FadeIn from "../../../src/vendored/FadeIn";
import { PieChart, pieChartDefaultProps } from "react-minimal-pie-chart";
import { useParams } from "react-router";
import ReactTooltip from "react-tooltip";
import { GuildData, PollType, VoteData } from "../../../common/sesh_types";
import { time_format_utils } from "../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../src/api/trpc/rootRouter";
import { DashboardUtils, FADE_IN_ANIMATION_DURATION } from "./dashboard_utils";
import { CSVDownload } from "./forms/improved_components/csv_download";
import { PollResultsVoterList } from "./forms/improved_components/poll_results_voter_list";
import { parseTimePollOption } from "./forms/poll_options";
import { trpc } from "./utils/trpc";
import { setDocTitle } from "./web_ui";

type OldVoteDataWithIndex = VoteData & { original_index: number };

type VoteDataWithIndex = {
	title: string;
	votes: number;
	original_index: number;
	percentage: number;
};

interface PollResultsProps {
	guild_data: GuildData;
	poll: RouterInferenceHelpers["poll"]["getListItem"]["output"];
	unique_vote_count: number;
	options: RouterInferenceHelpers["poll"]["votesOverview"]["output"];
}

interface PollResultsState {
	sorted_data: VoteDataWithIndex[];
	chart_selected_index?: number;
	chart_hovered_index?: number;
	should_display_tooltip: boolean;
	user_hover_name: string;
}

interface ChartData {
	key: number;
	title: string;
	value: number;
	color: string;
	original_index: number;
}

function limitStringLength(str: string, characters: number, suffix = "") {
	if (str.length <= characters) {
		return str;
	}

	return str.substring(0, characters - suffix.length) + suffix;
}

const chart_colors = [
	"#4D4D4D",
	"#5DA5DA",
	"#FAA43A",
	"#60BD68",
	"#F17CB0",
	"#B2912F",
	"#B276B2",
	"#DECF3F",
	"#F15854",
	"#D84797",
];

// export async function fetchPollResults(
// 	auth_token: DiscordAuthToken,
// 	guild_id: string,
// 	message_id: string
// ) {
// 	const request_body: GetPollResultsRequest = {
// 		guild_id,
// 		message_id,
// 		access_token: auth_token.access_token,
// 		token_type: auth_token.token_type,
// 	};

// 	return fetch(`/api/get_poll_results`, {
// 		method: "POST",
// 		headers: {
// 			"Content-Type": "application/json",
// 		},
// 		body: JSON.stringify(request_body),
// 	}).then((response) =>
// 		response.json().then((value: DashboardResponse<PollResultsProps>) => {
// 			if (value.err) {
// 				DashboardUtils.ShowDefaultErrorAlert(value.err);
// 				return;
// 			}

// 			return value.props;
// 		})
// 	);
// }

const darkened_colors = chart_colors.map((val) => shadeColor(val, -25));

const PIE_CHART_RADIUS = pieChartDefaultProps.radius - 20;
const INNER_RADIUS = 50;

function shadeColor(color: string, percent: number) {
	let R = parseInt(color.substring(1, 3), 16);
	let G = parseInt(color.substring(3, 5), 16);
	let B = parseInt(color.substring(5, 7), 16);

	R = Math.round((R * (100 + percent)) / 100);
	G = Math.round((G * (100 + percent)) / 100);
	B = Math.round((B * (100 + percent)) / 100);

	R = R < 255 ? R : 255;
	G = G < 255 ? G : 255;
	B = B < 255 ? B : 255;

	let RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
	let GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
	let BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

	return "#" + RR + GG + BB;
}

export class PollResultsForm extends React.Component<
	PollResultsProps,
	PollResultsState
> {
	constructor(props) {
		super(props);

		let sorted_data: VoteDataWithIndex[] = this.props.options
			.map((val, index) => {
				{
					return {
						title: val.name,
						votes: val.vote_count,
						percentage:
							val.vote_count /
							Math.max(
								this.props.poll.vote_count,
								Number.EPSILON
							),
						original_index: index,
					};
				}
			})
			.sort((a, b) => b.votes - a.votes);

		setDocTitle("Poll results", this.props.guild_data.name);

		this.state = {
			sorted_data: sorted_data,
			chart_selected_index: null,
			chart_hovered_index: null,
			should_display_tooltip: true,
			user_hover_name: null,
		};

		this.onChartClicked = this.onChartClicked.bind(this);
		this.onChartMouseOver = this.onChartMouseOver.bind(this);
		this.onChartMouseOut = this.onChartMouseOut.bind(this);
		this.getOptionTitle = this.getOptionTitle.bind(this);
	}

	getOptionTitle(data: VoteDataWithIndex) {
		const is_time_poll = this.props.poll.type === PollType.Time;
		if (!is_time_poll) {
			return data.title ? data.title : "unnamed";
		}

		const { date, creator_string } = parseTimePollOption(data.title);

		let title = time_format_utils.format_time_concise_date_first(
			luxon.DateTime.fromJSDate(date)
		);

		if (creator_string) {
			title += ` ${creator_string}`;
		}

		return title;
	}

	getTrueIndex(sorted_index: number) {
		if (sorted_index == null) {
			return null;
		}

		return this.state.sorted_data[sorted_index].original_index;
	}

	onChartClicked(event, index: number) {
		if (index === this.state.chart_selected_index) {
			index = null;
		}
		this.setState({
			chart_selected_index: index,
		});
	}

	onChartMouseOver(
		event,
		index: number,
		should_display_tooltip: boolean,
		should_ignore = false
	) {
		if (should_ignore) {
			return;
		}

		this.setState({
			chart_hovered_index: index,
			should_display_tooltip: should_display_tooltip,
		});
	}

	onChartMouseOut(event, index) {
		this.setState({ chart_hovered_index: null });
	}

	getChartData(data: VoteDataWithIndex[]): ChartData[] {
		return data.map((val, index) => {
			const normalized_color_index = index % chart_colors.length;

			return {
				key: index,
				title: this.getOptionTitle(val),
				value: val.percentage,
				original_index: val.original_index,
				color:
					this.state.chart_hovered_index === index
						? darkened_colors[normalized_color_index]
						: chart_colors[normalized_color_index],
			};
		});
	}

	createPieChartBulletPoint(datum: ChartData, index: number) {
		const has_votes = datum.value > 0;

		const text_class =
			this.state.chart_selected_index === index
				? "has-text-weight-bold"
				: "";

		return (
			<div
				key={index}
				className="columns is-variable is-1 pie-chart-list is-mobile"
				onMouseOver={
					has_votes
						? (event) => this.onChartMouseOver(event, index, false)
						: undefined
				}
				onMouseOut={(event) => this.onChartMouseOut(event, index)}
				onClick={
					has_votes
						? (event) => this.onChartClicked(event, index)
						: undefined
				}
			>
				<div className="column is-narrow pie-chart-list">
					<div
						className="color-box pie-chart-list"
						style={{ backgroundColor: datum.color }}
					></div>
				</div>
				<div className="column pie-chart-list">
					<b>{`${
						Math.round((datum.value * 100 + Number.EPSILON) * 100) /
						100
					}%`}</b>
					<span className={text_class}>{` ${datum.title}`}</span>
				</div>
			</div>
		);
	}

	renderPieChartList(data: ChartData[]) {
		let data_list = [];
		let index = 0;
		for (let datum of data) {
			data_list.push(this.createPieChartBulletPoint(datum, index));
			index++;
		}

		return <ul>{data_list}</ul>;
	}

	makePieChartHoverTooltip(entry: VoteDataWithIndex) {
		return (
			<div>
				<p>
					<b>{this.getOptionTitle(entry)}</b>
					<br />
					Votes:{" "}
					<b>
						{entry.votes} (
						{Math.round(
							(entry.percentage * 100 + Number.EPSILON) * 100
						) / 100}
						%)
					</b>
				</p>
			</div>
		);
	}

	createVoterList(chartData: ChartData[]) {
		let current_raw_data =
			this.state.sorted_data[this.state.chart_selected_index];

		let is_all = !current_raw_data;

		let users_elements = [];
		let index = 0;

		// for (let user of voter_names) {
		// 	let options_user_voted_for = this.state.user_vote_map.get(user);

		// 	let user_name =
		// 		user.length > 13 ? user.substring(0, 13) + "..." : user;

		// 	let element = (
		// 		<div
		// 			key={index}
		// 			onMouseOver={() => this.setState({ user_hover_name: user })}
		// 			onMouseOut={() => this.setState({ user_hover_name: null })}
		// 		>
		// 			<div>
		// 				<p>{user_name}</p>
		// 			</div>
		// 			<div>
		// 				{options_user_voted_for.map((val, index) => {
		// 					return (
		// 						<div
		// 							key={index}
		// 							className="color-box pie-chart-list"
		// 							style={{
		// 								backgroundColor: chartData[val].color,
		// 							}}
		// 						></div>
		// 					);
		// 				})}
		// 			</div>
		// 		</div>
		// 	);

		// 	users_elements.push(element);

		// 	index++;
		// }

		// const first_third_index = Math.ceil(users_elements.length / 3);
		// const second_third_index = Math.ceil((users_elements.length * 2) / 3);

		const tab_element_descriptors = this.state.sorted_data
			.map((val, index) => {
				return {
					element: (
						<li
							key={index}
							className={
								index === this.state.chart_selected_index
									? "is-active"
									: ""
							}
							onClick={() =>
								this.setState({
									chart_selected_index: index,
								})
							}
						>
							<a title={this.getOptionTitle(val)}>
								{limitStringLength(
									this.getOptionTitle(val),
									16,
									"..."
								)}
							</a>
						</li>
					),
					index: val.original_index,
				};
			})
			.sort((a, b) => a.index - b.index);

		return (
			<div>
				<div className="tabs poll-tabs is-boxed !mb-0">
					<ul>
						<li
							className={is_all ? "is-active" : ""}
							onClick={() => {
								this.setState({
									chart_selected_index: null,
								});
							}}
						>
							<a>All</a>
						</li>
						{tab_element_descriptors.map((val) => val.element)}
					</ul>
				</div>
				<div>
					<PollResultsVoterList
						key={current_raw_data?.original_index}
						option_index={current_raw_data?.original_index}
						voteBoxCreator={(indices, include_title) => (
							<VoteColorBox
								option_indices={indices}
								chart_data={this.getChartData(
									this.state.sorted_data
								)}
								include_title={include_title}
							/>
						)}
					/>
				</div>
				{/* <div className="columns is-centered is-mobile">
					<div className="column is-full">
						<div
							className="columns is-multiline is-mobile is-centered"
							data-tip=""
							data-for="user-hover"
						>
							<div className="column is-one-third">
								<ul>
									{users_elements.slice(0, first_third_index)}
								</ul>
							</div>
							<div className="column is-one-third">
								<ul>
									{users_elements.slice(
										first_third_index,
										second_third_index
									)}
								</ul>
							</div>
							<div className="column is-one-third">
								<ul>
									{users_elements.slice(
										second_third_index,
										users_elements.length
									)}
								</ul>
							</div>
						</div>
						<ReactTooltip
							id="user-hover"
							getContent={() => {
								return typeof this.state.user_hover_name ===
									"string"
									? this.makeUserHoverTooltip(
											chartData,
											this.state.user_hover_name
									  )
									: null;
							}}
						/>
					</div>
				</div> */}
			</div>
		);
	}

	// downloadCSVFile() {
	// 	let csv_lines: string[][] = [];
	// 	for (let data of this.state.sorted_data) {
	// 		let current_line_elements: string[] = [];
	// 		current_line_elements.push(data.title);
	// 		current_line_elements.push(data.votes.toString());
	// 		current_line_elements.push(`${data.voter_names.join(",")}`);
	// 		csv_lines.push(current_line_elements);
	// 	}

	// 	stringify(csv_lines, (err, output) => {
	// 		if (err) {
	// 			DashboardUtils.ShowDefaultErrorAlert(err.toString());
	// 			return;
	// 		}

	// 		const element = document.createElement("a");

	// 		const file = new Blob([output], { type: "text/plain" });
	// 		element.href = URL.createObjectURL(file);
	// 		element.download = `poll_results_${this.props.message_id}.csv`;
	// 		document.body.appendChild(element); // Required for this to work in FireFox
	// 		element.click();
	// 	});
	// }

	render() {
		const chartData = this.getChartData(this.state.sorted_data);

		return (
			<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
				<div
					className="columns is-centered is-mobile"
					style={{ transition: "opacity 0.5s", opacity: "1" }}
				>
					<div className="column is-11-mobile is-three-quarters-tablet is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd create-event-container">
						<DashboardUtils.RenderLinkChain
							link_chain_data={[
								DashboardUtils.GetGuildSelectionLinkChainData(),
								DashboardUtils.RenderGuildOptionSelectLinkChain(
									this.props.guild_data
								),
								DashboardUtils.RenderPollSelectionLinkChain(
									this.props.guild_data
								),
								DashboardUtils.RenderPollResultsLinkChain(
									this.props.guild_data,
									this.props.poll.message_id
								),
							]}
						/>
						<h1 className="title is-3 has-text-centered !mb-8">
							<i className="fas fa-poll fa-lg title-icon"></i>
							<i>{this.props.poll.poll_name}</i> Data
						</h1>
						<div className="columns is-multiline is-mobile">
							<div className="column is-narrow">
								Total Votes
								<br></br>
								Unique Voters
								{this.props.poll.is_anonymous && (
									<span>
										<br />
										Anonymous Poll
									</span>
								)}
								<br></br>
							</div>
							<div className="column is-narrow">
								<b>
									{this.props.poll.vote_count}
									<br></br>
									{this.props.unique_vote_count}
								</b>
							</div>
						</div>

						<div className="columns is-vcentered is-centered is-multiline is-mobile">
							<div className="column is-one-third">
								{this.renderPieChartList(chartData)}
							</div>
							<div className="column is-two-thirds">
								<div className="columns is-multiline is-mobile">
									{this.renderPieChart(
										chartData,
										this.props.poll.vote_count > 0
									)}
									<div className="column">
										<div className="columns">
											<div className="column">
												<div className="is-pulled-right">
													<ResultsCSVDownload />
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						{this.createVoterList(chartData)}
					</div>
				</div>
			</FadeIn>
		);
	}

	renderPieChart(chartData: ChartData[], has_votes_recorded: boolean) {
		if (!has_votes_recorded) {
			return (
				<div className="column is-full">
					<div className="columns is-multiline is-centered is-vcentered">
						<div className="column is-full"></div>
						<div className="column is-full"></div>
						<div className="column is-full">No votes recorded.</div>
						<div className="column is-full"></div>
						<div className="column is-full"></div>
					</div>
				</div>
			);
		}

		return (
			<div className="column is-full" data-tip="" data-for="chart">
				<PieChart
					//totalValue={1000}
					data={chartData.filter((val) => val.value > 0)}
					// TODO: don't add '...' if char limit isnt reached
					label={({ dataEntry }) => {
						let stringified_name = dataEntry.title.toString();
						return stringified_name.length > 10
							? stringified_name.substring(0, 10) + "..."
							: stringified_name;
					}}
					labelStyle={{
						fill: "#fff",
						opacity: 0.75,
						fontSize: "0.2em",
					}}
					labelPosition={112}
					radius={PIE_CHART_RADIUS}
					lineWidth={INNER_RADIUS}
					segmentsStyle={{
						transition: "stroke .3s",
						cursor: "pointer",
					}}
					animate
					segmentsShift={(index) => {
						if (index === this.state.chart_selected_index) {
							return 6;
						}
						if (index === this.state.chart_hovered_index) {
							return 2;
						}
						return 0;
					}}
					onClick={this.onChartClicked}
					onMouseOver={(event, index) =>
						this.onChartMouseOver(event, index, true)
					}
					onMouseOut={this.onChartMouseOut}
				/>
				<ReactTooltip
					id="chart"
					getContent={() => {
						return typeof this.state.chart_hovered_index ===
							"number" && this.state.should_display_tooltip
							? this.makePieChartHoverTooltip(
									this.state.sorted_data[
										this.state.chart_hovered_index
									]
							  )
							: null;
					}}
				/>
			</div>
		);
	}
}

function ResultsCSVDownload() {
	let { server_id, message_id } = useParams<{
		server_id: string;
		message_id: string;
	}>();

	const mutation = trpc.poll.resultsCsv.useMutation({
		onSuccess: (data) => {
			stringify(data, (err, output) => {
				if (err) {
					DashboardUtils.ShowDefaultErrorAlert(err.toString());
					return;
				}

				const element = document.createElement("a");

				const file = new Blob([output], { type: "text/plain" });
				element.href = URL.createObjectURL(file);
				element.download = `poll_results_${message_id}.csv`;
				document.body.appendChild(element); // Required for this to work in FireFox
				element.click();
			});
		},
	});

	return (
		<CSVDownload
			is_loading={mutation.isLoading}
			onClick={() =>
				mutation.mutate({
					guild_id: server_id,
					message_id,
				})
			}
		/>
	);
}

export function VoteColorBox(props: {
	option_indices: number[];
	chart_data: ChartData[];
	include_title?: boolean;
}) {
	const sorted_indices = [...props.option_indices].sort((a, b) => a - b);

	return (
		<>
			{sorted_indices.map((val, index) => {
				const chart_data = props.chart_data.find(
					(data) => data.original_index === val
				);
				if (!chart_data) {
					return <></>;
				}

				return (
					<div className="flex items-center gap-2" key={val}>
						<div
							className="color-box pie-chart-list"
							style={{
								backgroundColor: chart_data.color,
							}}
						></div>
						{props.include_title && <div>{chart_data.title}</div>}
					</div>
				);
			})}
		</>
	);
}
